import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { User, IdCard, CreditCard, Repeat, LifeBuoy, HelpCircle, LogOut, ChevronRight, ArrowLeft } from 'lucide-react';

export default function ProfileHub() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
    }
  }, [user, navigate]);

  if (!user || user.user_type !== 'client') return null;

  const initial = (user.name || '?').trim().charAt(0).toUpperCase();

  const menu = [
    { key: 'personal', label: 'Dados pessoais', desc: 'Ver seus dados cadastrais', icon: IdCard, to: '/client/personal-data' },
    { key: 'plan', label: 'Assinatura', desc: 'Ver detalhes da sua assinatura', icon: CreditCard, to: '/client/plan-details' },
    { key: 'migrate', label: 'Migrar plano', desc: 'Trocar de plano', icon: Repeat, to: '/client/migrate-plan' },
    { key: 'support', label: 'Suporte', desc: 'Falar com o suporte', icon: LifeBuoy, to: '/client/support' },
    { key: 'help', label: 'Central de Ajuda', desc: 'Dúvidas frequentes', icon: HelpCircle, to: '/client/help' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="page-profile">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')} data-testid="profile-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-lg font-bold" data-testid="profile-title">Perfil</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Identidade */}
        <Card className="p-5 flex items-center gap-4" data-testid="profile-identity">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-xl font-bold text-primary" data-testid="profile-avatar-initial">{initial}</span>
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-base truncate flex items-center gap-2" data-testid="profile-name">
              <User className="w-4 h-4 text-slate-500 shrink-0" />
              {user.name || 'Cliente'}
            </p>
            {user.email && (
              <p className="text-sm text-muted-foreground truncate" data-testid="profile-email">{user.email}</p>
            )}
          </div>
        </Card>

        {/* Menu de atalhos (HUB de navegação) */}
        <div className="space-y-3">
          {menu.map(({ key, label, desc, icon: Icon, to }) => (
            <Card
              key={key}
              className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => navigate(to)}
              data-testid={`profile-link-${key}`}
            >
              <div className="bg-slate-100 p-2 rounded-full shrink-0">
                <Icon className="w-5 h-5 text-slate-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{label}</p>
                <p className="text-sm text-muted-foreground truncate">{desc}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
            </Card>
          ))}
        </div>

        {/* Logout */}
        <Button
          variant="outline"
          className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          onClick={handleLogout}
          data-testid="profile-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sair
        </Button>
      </div>
    </div>
  );
}
